import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useAgentCommands } from '../contexts/AgentCommandContext';
import { useI18n } from '../i18n';

/**
 * Utilidad para esperar a que un elemento aparezca en el DOM.
 */
const waitForElement = (selector, timeout = 3000) => {
  return new Promise((resolve, reject) => {
    const el = document.querySelector(selector);
    if (el) return resolve(el);

    const observer = new MutationObserver(() => {
      const target = document.querySelector(selector);
      if (target) {
        resolve(target);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Timeout esperando el elemento: ${selector}`));
    }, timeout);
  });
};

/**
 * Helper para inyectar valores en inputs de React disparando los eventos necesarios.
 */
const setNativeValue = (element, value) => {
  const valueSetter = Object.getOwnPropertyDescriptor(element, 'value').set;
  const prototype = Object.getPrototypeOf(element);
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value').set;

  if (valueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(element, value);
  } else {
    valueSetter.call(element, value);
  }
  element.dispatchEvent(new Event('input', { bubbles: true }));
};

/**
 * Busca un elemento interactivo por texto dentro de un contenedor y hace click.
 */
const clickByText = (containerSelector, searchText) => {
  const container = document.querySelector(containerSelector);
  if (!container) return false;

  const interactives = container.querySelectorAll('button, a, div[role="button"]');
  let bestMatch = null;
  const normalizedSearch = searchText.toLowerCase();

  for (const el of interactives) {
    const text = el.innerText.toLowerCase();
    if (text.includes(normalizedSearch)) {
      // Elegimos la coincidencia más corta para mayor precisión
      if (!bestMatch || text.length < bestMatch.innerText.length) {
        bestMatch = el;
      }
    }
  }

  if (bestMatch) {
    triggerAgentFeedback();
    bestMatch.click();
    return true;
  }
  return false;
};

const triggerAgentFeedback = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('q-agent-manipulate'));
};


export const DEFERRED_ACTION_TIMEOUT_MS = 8000;

export const shouldSpeakBeforeExecution = (type) => ['NAVIGATE', 'CONVERSATION', 'ERROR'].includes(type);

export const taskCommandForAction = (action) => {
  if (action === 'draft_task') return 'tasks_prepare_task';
  if (action === 'create_task') return 'tasks_create_task';
  return null;
};

export const networkCommandForAction = (action) => {
  if (action === 'open_groups') return 'network_open_groups';
  if (action === 'close_groups') return 'network_close_groups';
  if (action === 'read_groups') return 'network_read_groups';
  if (action === 'send_message') return 'network_send_message';
  if (action === 'toggle_bottom_bar') return 'network_toggle_bottom_bar';
  return null;
};

export const concienciaCommandForAction = (action) => {
  const map = {
    cycle_main_options: 'conciencia_cycle_main',
    cycle_secondary_options: 'conciencia_cycle_secondary',
    stop_cycle_options: 'conciencia_stop_cycle',
    select_main_option: 'conciencia_select_main',
    select_secondary_option: 'conciencia_select_secondary',
    scroll_secondary: 'conciencia_scroll_secondary',
    next_question: 'conciencia_next_question',
    set_main_option: 'conciencia_set_main_option',
    set_secondary_option: 'conciencia_set_secondary_option'
  };
  return map[action] || null;
};

export const isAgentCommandSuccess = (result) => {
  if (result && typeof result === 'object') return result.ok === true;
  return Boolean(result);
};

export const taskSpeechForResult = (action, result, agentSay = (es) => es) => {
  const ok = isAgentCommandSuccess(result);
  if (action === 'create_task') {
    return ok
      ? agentSay('Tarea creada.', 'Task created.')
      : agentSay('No pude crear esa tarea todavía.', 'I could not create that task yet.');
  }
  return ok
    ? agentSay('Dejé la tarea preparada.', 'I prepared the task.')
    : agentSay('No pude preparar esa tarea todavía.', 'I could not prepare that task yet.');
};

export const createPendingAgentAction = (action, targetRoute, sourceRoute = '', now = Date.now()) => ({
  id: `${now}-${Math.random().toString(36).slice(2)}`,
  action,
  targetRoute,
  sourceRoute,
  expiresAt: now + DEFERRED_ACTION_TIMEOUT_MS
});

export const getPendingAgentActionStatus = (pending, pathname, now = Date.now()) => {
  if (!pending) return { status: 'empty' };
  if (pending.expiresAt <= now) return { status: 'expired' };
  if (pending.targetRoute && pathname !== pending.targetRoute) {
    if (pathname !== pending.sourceRoute) return { status: 'clear' };
    return { status: 'wait' };
  }
  return { status: 'run', action: pending.action };
};

export const applyAgentSetting = (payload = {}) => {
  const action = String(payload.action || '').toLowerCase();
  const rawValue = String(payload.value || payload.theme || payload.target || payload.text || '').toLowerCase();

  if (action.includes('theme') || rawValue === 'light' || rawValue === 'dark') {
    const theme = rawValue === 'dark' || payload.theme === 'dark' ? 'dark' : rawValue === 'light' || payload.theme === 'light' ? 'light' : null;
    if (!theme) return { ok: false };
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('darkMode', theme === 'dark' ? 'true' : 'false');
    } catch (_) {}
    window.dispatchEvent(new CustomEvent('q-theme-mode-change', { detail: theme }));
    return { ok: true, kind: 'theme', value: theme };
  }

  if (action.includes('cyberpunk') || rawValue.includes('cyberpunk')) {
    const enabled = payload.enabled !== undefined
      ? Boolean(payload.enabled)
      : !['false', 'off', 'disable', 'disabled'].includes(rawValue);
    const value = enabled ? 'true' : 'false';

    document.documentElement.setAttribute('data-cyberpunk', value);
    try {
      localStorage.setItem('cyberpunkMode', value);
    } catch (_) {}
    window.dispatchEvent(new CustomEvent('q-cyberpunk-mode-change', { detail: enabled }));

    if (enabled) {
      document.documentElement.setAttribute('data-solarpunk', 'false');
      try { localStorage.setItem('solarpunkMode', 'false'); } catch (_) {}
      window.dispatchEvent(new CustomEvent('q-solarpunk-mode-change', { detail: false }));
    }

    return { ok: true, kind: 'cyberpunk', value: enabled };
  }

  if (action.includes('solarpunk') || rawValue.includes('solarpunk')) {
    const enabled = payload.enabled !== undefined
      ? Boolean(payload.enabled)
      : !['false', 'off', 'disable', 'disabled'].includes(rawValue);
    const value = enabled ? 'true' : 'false';

    document.documentElement.setAttribute('data-solarpunk', value);
    try {
      localStorage.setItem('solarpunkMode', value);
    } catch (_) {}
    window.dispatchEvent(new CustomEvent('q-solarpunk-mode-change', { detail: enabled }));

    if (enabled) {
      document.documentElement.setAttribute('data-cyberpunk', 'false');
      try { localStorage.setItem('cyberpunkMode', 'false'); } catch (_) {}
      window.dispatchEvent(new CustomEvent('q-cyberpunk-mode-change', { detail: false }));
    }

    return { ok: true, kind: 'solarpunk', value: enabled };
  }

  return { ok: false };
};

/**
 * Hook central que conecta el cerebro (Backend JSON) con el cuerpo (UI React).
 * Recibe instrucciones y ejecuta manipulaciones directas del DOM o navegación.
 */
export const useAgentOrchestrator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useI18n();
  const [agentMode, setAgentMode] = useState('IDLE');
  const [pendingActionVersion, setPendingActionVersion] = useState(0);
  const pendingDeferredAction = useRef(null);
  const pendingRouteAction = useRef(null);
  const pendingTimeoutsRef = useRef(new Set());
  const handleAgentActionRef = useRef(null);
  const speechRunIdRef = useRef(0);
  const { getCommand } = useAgentCommands();
  const agentSay = useCallback((es, en) => (language === 'en' ? en : es), [language]);

  const clearPendingTimeouts = useCallback(() => {
    pendingTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    pendingTimeoutsRef.current.clear();
  }, []);

  const clearAgentState = useCallback(() => {
    pendingDeferredAction.current = null;
    pendingRouteAction.current = null;
    clearPendingTimeouts();
    speechRunIdRef.current = Date.now();
    setAgentMode('IDLE');
    setPendingActionVersion((version) => version + 1);
    window.dispatchEvent(new CustomEvent('q-agent-thinking', { detail: false }));
    try {
      window.speechSynthesis?.cancel();
    } catch (_) {}
  }, [clearPendingTimeouts]);

  const enqueuePendingAction = useCallback((ref, action, targetRoute) => {
    if (!action) return;
    const pending = createPendingAgentAction(action, targetRoute, window.location.pathname);
    ref.current = pending;
    const timeoutId = window.setTimeout(() => {
      pendingTimeoutsRef.current.delete(timeoutId);
      if (ref.current?.id === pending.id) {
        ref.current = null;
        setAgentMode('IDLE');
        setPendingActionVersion((version) => version + 1);
        window.dispatchEvent(new CustomEvent('q-agent-thinking', { detail: false }));
      }
    }, DEFERRED_ACTION_TIMEOUT_MS);
    pendingTimeoutsRef.current.add(timeoutId);
    setPendingActionVersion((version) => version + 1);
  }, []);

  const speak = useCallback((text) => {
    if (!text) return;
    const runId = Date.now();
    speechRunIdRef.current = runId;
    window.speechSynthesis.cancel(); // Detener cualquier habla previa
    window.dispatchEvent(new Event('q-speech-suspend'));
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = document.documentElement.lang === 'en' ? 'en-US' : 'es-ES';
    utterance.rate = 1.0;
    const resumeSpeech = () => {
      if (speechRunIdRef.current === runId) {
        window.dispatchEvent(new Event('q-speech-resume'));
      }
    };
    utterance.onend = resumeSpeech;
    utterance.onerror = resumeSpeech;
    try {
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      resumeSpeech();
      throw error;
    }
  }, []);

  const cleanTextForSpeech = useCallback((rawText = '') => String(rawText)
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, '')
    .replace(/[^\S\r\n]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim(), []);

  const limitForSpeech = useCallback((text, max = 1800) => {
    const cleaned = cleanTextForSpeech(text);
    if (cleaned.length <= max) return cleaned;
    return `${cleaned.slice(0, max)}. ${agentSay('Texto recortado para lectura.', 'Text trimmed for reading.')}`;
  }, [agentSay, cleanTextForSpeech]);

  const safeSpeak = useCallback((text, fallback = agentSay('No encontré texto para leer.', 'I could not find text to read.')) => {
    const cleaned = cleanTextForSpeech(text);
    speak(cleaned || fallback);
  }, [agentSay, cleanTextForSpeech, speak]);


  const executeCommandWithAwait = useCallback(async (commandId, payload, timeout = 3000) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeout) {
      const command = getCommand(commandId);
      if (typeof command === 'function') {
        triggerAgentFeedback();
        return await Promise.resolve(command(payload));
      }
      await new Promise((resolve) => setTimeout(resolve, 120));
    }
    throw new Error(`Timeout esperando comando: ${commandId}`);
  }, [getCommand]);

  const routeForAction = useCallback((type, payload) => {
    if (type === 'NETWORK_OPS') return '/network';
    if (type === 'QOPH_OPS') return '/qoph';
    const map = { TASKS_OPS:'/tareas', PLACES_OPS:'/lugares', PROFILE_OPS:'/profile', CONSCIOUSNESS_OPS:'/conciencia', CONCIENCIA_OPS:'/conciencia', MUTANT_BOX_OPS:'/', HYPERSTITION_OPS:'/hiperstision', QUESTIONNAIRE_OPS:'/questionnaire', GALLERY_OPS:'/profile/gallery' };
    if (map[type]) return map[type];
    return payload?.route || null;
  }, []);

  const normalizeText = useCallback((value = '') => String(value).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim(), []);
  const findByApproxText = useCallback((elements = [], target = '') => {
    const normalizedTarget = normalizeText(target);
    return elements.find((el) => normalizeText(el?.innerText || el?.getAttribute?.('data-agent-action-label') || '').includes(normalizedTarget));
  }, [normalizeText]);
  const safeClick = useCallback((element, fallbackMessage = agentSay('No encontré ese control en pantalla.', 'I could not find that control on screen.')) => {
    if (!element) { speak(fallbackMessage); return true; }
    triggerAgentFeedback();
    element.click();
    return true;
  }, [agentSay, speak]);
  const scrollToMutantBox = useCallback(() => {
    const box = document.getElementById('mutant-box-container') || document.getElementById('mutant-box-main') || document.querySelector('[data-agent-module="mutant-box"]') || document.querySelector('.mutantbox-root');
    if (!box) { speak(agentSay('No encontré la Caja Mutante en esta pantalla.', 'I could not find the Mutant Box on this screen.')); return false; }
    triggerAgentFeedback();
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, [agentSay, speak]);
  const findByDataAttribute = useCallback((selector, attrName, target) => {
    const normalizedTarget = normalizeText(target || '');
    return Array.from(document.querySelectorAll(selector)).find((el) => {
      const value = el.getAttribute(attrName) || el.innerText || '';
      return normalizeText(value).includes(normalizedTarget);
    });
  }, [normalizeText]);

  const isSensitiveAction = (action = '') => ['delete','remove','privacy','logout','export','submit'].some((k) => String(action).includes(k));

  const handleTasksOps = useCallback(async (payload = {}) => {
    const action = payload.action;
    if (action === 'read_tasks') {
      const items = Array.from(document.querySelectorAll('[data-agent-task-title], .task-main-title, .original-prompt')).slice(0, 8).map((el) => el.getAttribute('data-agent-task-title') || el.innerText).filter(Boolean);
      return safeSpeak(items.length ? agentSay(`Tus tareas visibles: ${items.join(', ')}`, `Your visible tasks: ${items.join(', ')}`) : '', agentSay('No encontré tareas visibles.', 'I could not find visible tasks.'));
    }
    if (action === 'read_next_task') {
      const first = document.querySelector('[data-agent-task-title], .task-main-title, .original-prompt');
      return safeSpeak(first?.getAttribute?.('data-agent-task-title') || first?.innerText, agentSay('No encontré una próxima tarea.', 'I could not find a next task.'));
    }
    if (action === 'draft_task' || action === 'create_task') {
      const text = String(payload.text || '').trim();
      const commandId = taskCommandForAction(action);
      if (!text) {
        return speak(action === 'create_task'
          ? agentSay('No encontré el texto de la tarea para crear.', 'I could not find the task text to create.')
          : agentSay('No encontré el texto de la tarea para preparar.', 'I could not find the task text to prepare.'));
      }
      try {
        const result = await executeCommandWithAwait(commandId, text, 5000);
        setAgentMode('IDLE');
        const ok = isAgentCommandSuccess(result);
        speak(taskSpeechForResult(action, result, agentSay));
        return ok;
      } catch (error) {
        console.error('[AgentQ][TASKS_OPS]', error);
        setAgentMode('IDLE');
        speak(action === 'create_task'
          ? agentSay('No pude crear esa tarea todavía.', 'I could not create that task yet.')
          : agentSay('No pude preparar esa tarea todavía.', 'I could not prepare that task yet.'));
        return false;
      }
    }
    speak(agentSay('No pude ejecutar esa acción de tareas todavía.', 'I cannot execute that task action yet.'));
    return false;
  }, [agentSay, executeCommandWithAwait, safeSpeak, speak]);

  const handleTaskOps = useCallback(async (payload = {}) => {
    const action = payload.action || (payload.text ? 'draft_task' : null);
    const text = payload.text || payload.draft;

    if (action === 'draft' || action === 'draft_task' || payload.draft) {
      return handleTasksOps({ action: 'draft_task', text });
    }

    if (action === 'confirm' || payload.confirm) {
      setAgentMode('IDLE');
      speak(agentSay('La tarea está preparada. Confirmala desde el formulario cuando quieras crearla.', 'The task is prepared. Confirm it from the form when you want to create it.'));
      return true;
    }

    if (action === 'delete' || payload.delete) {
      const deleteBtn = document.querySelector('.btn-delete-task-active');
      if (deleteBtn) {
        triggerAgentFeedback();
        deleteBtn.click();
        return true;
      }
      speak(agentSay('No encontré una tarea activa para borrar.', 'I could not find an active task to delete.'));
      return false;
    }

    if (action === 'visualize') {
      return safeClick(document.getElementById('btn-task-visualize'), agentSay('No encontré el control de visuales de la tarea.', 'I could not find the task visuals control.'));
    }

    if (action === 'substeps') {
      return safeClick(document.getElementById('btn-task-substeps'), agentSay('No encontré el control de subpasos de la tarea.', 'I could not find the task substeps control.'));
    }

    speak(agentSay('No pude ejecutar esa acción de tareas todavía.', 'I cannot execute that task action yet.'));
    return false;
  }, [agentSay, handleTasksOps, safeClick, speak]);

  const handlePlacesOps = useCallback((payload = {}) => {
    const action = payload.action;
    if (action === 'read_places') {
      const places = Array.from(document.querySelectorAll('[data-agent-place-name]')).map((el) => el.getAttribute('data-agent-place-name'));
      return safeSpeak(places.length ? agentSay(`Lugares visibles: ${places.join(', ')}`, `Visible places: ${places.join(', ')}`) : '', agentSay('No encontré lugares visibles.', 'I could not find visible places.'));
    }
    if (['select_place','open_place_details'].includes(action)) {
      const el = findByDataAttribute('[data-agent-place-name]','data-agent-place-name',payload.target || payload.text); if (!el) return speak(agentSay('No encontré ese lugar.', 'I could not find that place.')); triggerAgentFeedback(); el.click();
    }
  }, [agentSay, findByDataAttribute, safeSpeak, speak]);

  const handleNetworkOps = useCallback(async (payload) => {
    try {
      const action = payload?.action;
      if (action === 'open_groups' || action === 'close_groups' || action === 'read_groups' || action === 'send_message' || action === 'toggle_bottom_bar') {
        const commandId = networkCommandForAction(action);
        const text = payload?.text || payload?.message;
        if (action === 'send_message' && !String(text || '').trim()) {
          return speak(agentSay('No encontré el texto para enviar al grupo.', 'I could not find the text to send to the group.'));
        }
        const result = await executeCommandWithAwait(commandId, action === 'send_message' ? text : undefined, action === 'send_message' ? 5000 : 3000);
        const ok = isAgentCommandSuccess(result);
        if (!ok) {
          return action === 'send_message'
            ? speak(agentSay('No encontré un grupo abierto para escribir.', 'I could not find an open group to write to.'))
            : speak(agentSay('No pude completar esa acción en Red Q.', 'I could not complete that Q Network action.'));
        }
        if (action === 'read_groups') {
          return safeSpeak(result?.text, agentSay('No encontré grupos visibles en Red Q.', 'I could not find visible groups in the Q Network.'));
        }
        if (action === 'send_message') return speak(agentSay('Mensaje enviado al grupo.', 'Message sent to the group.'));
        if (action === 'open_groups') return speak(agentSay('Abrí los grupos de Red Q.', 'I opened Q Network groups.'));
        if (action === 'close_groups') return speak(agentSay('Cerré los grupos de Red Q.', 'I closed Q Network groups.'));
        return true;
      }
      if (action === 'read_manifesto') {
        const ok = await executeCommandWithAwait('network_read_manifesto');
        if (!ok) return speak(agentSay('No encontré un manifiesto legible en Red Q.', 'I could not find a readable manifesto in the Q Network.'));
        return true;
      }
      if (action === 'select_group') {
        const target = payload?.target || payload?.text;
        if (!target) return speak(agentSay('Decime qué grupo querés seleccionar.', 'Tell me which group you want to select.'));
        const ok = await executeCommandWithAwait('network_select_group', target);
        if (!ok) return speak(agentSay('No encontré ese grupo en Red Q.', 'I could not find that group in the Q Network.'));
      }
    } catch (error) {
      console.error('[AgentQ][NETWORK_OPS]', error);
      speak(agentSay('No pude completar esa acción en Red Q.', 'I could not complete that Q Network action.'));
    }
  }, [agentSay, executeCommandWithAwait, safeSpeak, speak]);

  const handleQophOps = useCallback(async (payload) => {
    try {
      const action = payload?.action;
      if (['open_history', 'open_brain', 'close_history', 'close_brain'].includes(action)) {
        const result = await executeCommandWithAwait(`qoph_${action}`);
        const ok = typeof result === 'object' ? result.ok !== false : Boolean(result);
        if (!ok) return speak(agentSay('No pude completar esa acción en Qoph.', 'I could not complete that Qoph action.'));
        return speak(action.startsWith('open')
          ? agentSay('Abrí el historial de Qoph.', 'I opened Qoph history.')
          : agentSay('Cerré el historial de Qoph.', 'I closed Qoph history.'));
      }
      if (action === 'read_last_message') {
        const ok = await executeCommandWithAwait('qoph_read_last_message');
        if (!ok) return speak(agentSay('No encontré una respuesta legible de Qoph.', 'I could not find a readable Qoph response.'));
        return;
      }
      if (action === 'send_message') {
        const text = payload?.text;
        if (!text || !text.trim()) return speak(agentSay('No encontré el texto que querés enviarle a Qoph.', 'I could not find the text you want to send to Qoph.'));
        const ok = await executeCommandWithAwait('qoph_send_message', text);
        if (!ok) return speak(agentSay('No pude enviarle ese pensamiento a Qoph.', 'I could not send that thought to Qoph.'));
      }
    } catch (error) {
      console.error('[AgentQ][QOPH_OPS]', error);
      speak(agentSay('No pude completar esa acción en Qoph.', 'I could not complete that Qoph action.'));
    }
  }, [agentSay, executeCommandWithAwait, speak]);

  const handleConsciousnessOps = useCallback((payload) => {
    // FALLBACK: Si el payload está vacío en este contexto, suele ser 'next'
    const action = payload?.action || 'next';
    const text = payload?.text || payload?.voice_feedback;

    if (action === 'choose_left' || payload?.choose_left) {
      triggerAgentFeedback();
      document.getElementById('proxy-btn-sphere-left')?.click();
    }
    if (action === 'choose_right' || payload.choose_right) {
      triggerAgentFeedback();
      document.getElementById('proxy-btn-sphere-right')?.click();
    }
    if (action === 'lock_selection') {
      triggerAgentFeedback();
      document.getElementById('proxy-btn-sphere-lock')?.click();
    }
    if (action === 'next' || payload.next) {
      triggerAgentFeedback();
      (document.getElementById('btn-consciousness-next') || document.getElementById('btn-generate-consciousness'))?.click();
    }

    if (action === 'voice_feedback' || payload.voice_feedback) {
      const area = document.getElementById('consciousness-feedback-area');
      if (area && text) {
        triggerAgentFeedback();
        setNativeValue(area, text);
        speak(agentSay("Te escucho, continúa o confirma.", "I hear you. Continue or confirm."));
      }
    }
  }, [agentSay, speak]);

  const handleConcienciaOps = useCallback(async (payload = {}) => {
    const action = payload.action;
    if (action === 'open_about') {
      navigate('/conciencia/about');
      speak(agentSay('Abriendo más sobre Conciencia.', 'Opening more about Consciousness.'));
      return true;
    }

    const commandId = concienciaCommandForAction(action);
    if (!commandId) return false;

    try {
      const result = await executeCommandWithAwait(commandId, payload, 4000);
      const ok = isAgentCommandSuccess(result);

      if (action.startsWith('cycle')) {
        if (!ok) return speak(agentSay('No pude mover esa esfera todavía.', 'I could not move that sphere yet.'));
        return speak(action.includes('secondary')
          ? agentSay('Rotando opciones secundarias.', 'Rotating secondary options.')
          : agentSay('Rotando opciones principales.', 'Rotating main options.'));
      }
      if (action === 'stop_cycle_options') return speak(agentSay('Rotación detenida.', 'Rotation stopped.'));
      if (action.startsWith('set_')) {
        if (!ok) return speak(agentSay('No pude mostrar esa opción.', 'I could not show that option.'));
        return speak(agentSay('Opción mostrada.', 'Option shown.'));
      }
      if (action.startsWith('select')) {
        if (!ok) return speak(agentSay('No encontré esa opción.', 'I could not find that option.'));
        return speak(agentSay('Opción seleccionada.', 'Option selected.'));
      }
      if (action === 'next_question') {
        if (!ok) return speak(agentSay('No pude pedir otra pregunta todavía.', 'I could not ask for another question yet.'));
        return speak(agentSay('Buscando otra pregunta.', 'Looking for another question.'));
      }
      return ok;
    } catch (error) {
      console.error('[AgentQ][CONCIENCIA_OPS]', error);
      speak(agentSay('Conciencia no está lista todavía.', 'Consciousness is not ready yet.'));
      return false;
    }
  }, [agentSay, executeCommandWithAwait, navigate, speak]);

  const handleConsciousnessPageOps = useCallback((payload = {}) => { if (payload.action === 'generate_reflection') { const btn = document.getElementById('btn-generate-consciousness') || document.getElementById('btn-consciousness-next'); if (btn){triggerAgentFeedback(); btn.click();}} if (payload.action === 'read_consciousness') safeSpeak(document.getElementById('consciousness-current')?.innerText || document.getElementById('consciousness-main')?.innerText, agentSay('No encontré contenido de conciencia.', 'I could not find consciousness content.')); }, [agentSay, safeSpeak]);

  const handleMutantOps = useCallback((payload) => {
    if (!payload) return;

    // Normalización: El backend puede enviar { button_click: N } o { action: 'button_click', index: N }
    let action = payload.action;
    let index = payload.index;
    let text = payload.text;

    if (payload.button_click !== undefined) {
      action = 'button_click';
      if (typeof payload.button_click === 'number') index = payload.button_click;
      if (typeof payload.button_click === 'string') text = payload.button_click;
    }

    if (action === 'new_signal' || payload.new_signal) {
      triggerAgentFeedback();
      document.getElementById('btn-mutant-signal')?.click();
    }

    if (action === 'button_click') {
      if (text) {
        speak(agentSay(`Seleccionando ${text}`, `Selecting ${text}`));
        const clicked = clickByText('.mutantbox-body', text);
        if (!clicked) console.warn(`[Orchestrator] No se encontró botón con texto: ${text}`);
      } else if (typeof index === 'number') {
        const btns = document.querySelectorAll('.mutant-action-btn');
        if (btns[index]) {
          speak(agentSay(`Seleccionando ${btns[index].innerText || 'botón'}`, `Selecting ${btns[index].innerText || 'button'}`));
          triggerAgentFeedback();
          btns[index].click();
        } else {
          console.warn(`[Orchestrator] No se encontró el botón mutante en el índice: ${index}`);
        }
      }
    }
  }, [agentSay, speak]);

  const handleMutantBoxOps = useCallback(async (payload = {}) => {
    try {
      const action = payload?.action || payload?.payload?.action;
      const target = payload?.target || payload?.text || payload?.payload?.target || payload?.payload?.text;
      if (action === 'scroll_to_box') return scrollToMutantBox();
      if (action === 'read_box' || action === 'read_last_mutation') { scrollToMutantBox(); const lead = document.getElementById('mutant-box-lead') || document.querySelector('[data-agent-readable="mutant-lead"]') || document.getElementById('mutant-box-current'); return safeSpeak(limitForSpeech(lead?.innerText || ''), agentSay('La Caja Mutante está en silencio o todavía no cargó contenido.', 'The Mutant Box is silent or has not loaded content yet.')); }
      if (action === 'generate_mutation') return safeClick(document.getElementById('btn-generate-mutation') || document.querySelector('[data-agent-action="generate-mutation"]'), agentSay('No encontré el botón para generar una nueva mutación.', 'I could not find the button to generate a new mutation.'));
      if (action === 'open_context') return safeClick(document.getElementById('btn-mutant-context') || document.querySelector('[data-agent-action="open-context"]'), agentSay('No encontré el botón de contexto de la Caja Mutante.', 'I could not find the Mutant Box context button.'));
      if (action === 'read_context') { let contextText = document.getElementById('mutant-box-context-text') || document.querySelector('[data-agent-readable="mutant-context"]'); if (!contextText) { safeClick(document.getElementById('btn-mutant-context') || document.querySelector('[data-agent-action="open-context"]')); await new Promise(r=>setTimeout(r,700)); contextText = document.getElementById('mutant-box-context-text') || document.querySelector('[data-agent-readable="mutant-context"]'); } return safeSpeak(limitForSpeech(contextText?.innerText || ''), agentSay('No encontré explicación contextual para esta mutación.', 'I could not find contextual explanation for this mutation.')); }
      if (action === 'toggle_explanation') return safeClick(document.getElementById('btn-mutant-explanation'));
      if (action === 'expand_image') return safeClick(document.getElementById('btn-mutant-expand-image') || document.querySelector('[data-agent-action="expand-image"]') || document.getElementById('mutant-box-image'), agentSay('No encontré una imagen para expandir.', 'I could not find an image to expand.'));
      if (action === 'collapse_image') return safeClick(document.getElementById('btn-mutant-collapse-image') || document.querySelector('[data-agent-action="collapse-image"]'), agentSay('No encontré el botón para cerrar la imagen.', 'I could not find the button to close the image.'));
      if (action === 'click_action' || action === 'select_action') { const buttons = Array.from(document.querySelectorAll('[data-agent-mutant-action]')); const match = findByApproxText(buttons, target || ''); if (!match) return speak(target ? agentSay(`No encontré la opción ${target} en la Caja Mutante.`, `I could not find the ${target} option in the Mutant Box.`) : agentSay('Decime qué opción de la Caja Mutante querés elegir.', 'Tell me which Mutant Box option you want to choose.')); return safeClick(match); }
      if (action === 'save_mutation') return safeClick(document.querySelector('[data-agent-action="save-mutation"]') || document.querySelector('[aria-label*="guardar" i]'), agentSay('No encontré un botón para guardar esta mutación.', 'I could not find a button to save this mutation.'));
      speak(agentSay('No reconocí esa acción para la Caja Mutante.', 'I did not recognize that Mutant Box action.'));
      return true;
    } catch (error) { console.error('[AgentQ][MUTANT_BOX_OPS]', error); speak(agentSay('Tuve un problema al intervenir la Caja Mutante.', 'I had a problem intervening in the Mutant Box.')); return true; }
  }, [agentSay, findByApproxText, limitForSpeech, safeClick, safeSpeak, scrollToMutantBox, speak]);

  const handleProfileOps = useCallback((payload) => {
      if (!payload) return;
      const { action, text } = payload;

      // 1. Feedback de texto
      if (text || action === 'feedback') {
          const input = document.getElementById('profile-feedback-input');
          if (input) {
              triggerAgentFeedback();
              input.value = text || '';
              input.dispatchEvent(new Event('input', { bubbles: true }));
              // Opcional: buscar botón de enviar y clickear
              setTimeout(() => {
                const submitBtn = document.getElementById('btn-profile-submit');
                if (submitBtn) {
                  triggerAgentFeedback();
                  submitBtn.click();
                }
              }, 500);
          }
      }

      // 2. Cambiar tema (Simulación)
      if (action === 'switch_theme' || action === 'toggle_theme') {
          triggerAgentFeedback();
          document.getElementById('btn-theme-toggle')?.click();
      }
  }, []);

  const handleQuestionnaireOps = useCallback((payload) => {
    if (!payload) return;
    const { action, index } = payload;

    if (action === 'select_option' && typeof index === 'number') {
      const btn = document.getElementById(`btn-quest-opt-${index}`);
      if (btn) {
        triggerAgentFeedback();
        btn.click();
      }
    }

    if (action === 'next' || payload.next) {
      const nextBtn = document.getElementById('btn-quest-next');
      if (nextBtn) {
        triggerAgentFeedback();
        nextBtn.click();
      }
    }

    if (action === 'skip' || payload.skip) {
      const btn = document.getElementById('btn-quest-skip');
      if (btn) {
        triggerAgentFeedback();
        btn.click();
      }
    }
  }, []);

  const handleSphereOps = useCallback((payload) => {
    if (!payload) return;
    const { action, text } = payload;

    if (action === 'rotate') {
      const rotateBtn = document.getElementById('btn-sphere-rotate');
      if (rotateBtn) {
        triggerAgentFeedback();
        rotateBtn.click();
      } else {
        console.warn('[Orchestrator] No se encontró el botón de rotación (#btn-sphere-rotate)');
      }
    }

    if (action === 'select' && text) {
      const targetText = text.toLowerCase().trim();
      const options = document.querySelectorAll('[data-option-text]');
      let found = null;

      for (let opt of options) {
        const optVal = (opt.getAttribute('data-option-text') || '').toLowerCase();
        if (optVal === targetText) {
          found = opt;
          break;
        }
        if (optVal.includes(targetText)) {
          found = opt;
        }
      }

      if (found) {
        triggerAgentFeedback();
        found.click();
      } else {
        console.warn(`[Orchestrator] No se encontró opción 3D para: ${text}`);
      }
    }
  }, []);

  const handleReadScreen = useCallback(() => {
    try {
      const path = window.location.pathname;
      let textToRead = "";

      if (path.includes('/network')) {
        const manifesto = document.getElementById('network-selected-manifesto') || document.querySelector('.manifesto-box');
        if (manifesto?.innerText) {
          safeSpeak(limitForSpeech(manifesto.innerText), agentSay('El manifiesto está vacío o no pude leerlo.', 'The manifesto is empty or I could not read it.'));
          return;
        }
        const groups = Array.from(document.querySelectorAll('[data-agent-group-name]'))
          .map((el) => el.getAttribute('data-agent-group-name'))
          .filter(Boolean);
        if (groups.length) {
          speak(agentSay(`Estás en La Red Q. Grupos visibles: ${groups.join(', ')}.`, `You are in the Q Network. Visible groups: ${groups.join(', ')}.`));
          return;
        }
      }

      if (path.includes('/qoph')) {
        const qophMessages = Array.from(document.querySelectorAll('[data-agent-role="qoph"], .chat-bubble.qoph'));
        const lastMessage = qophMessages[qophMessages.length - 1];
        if (lastMessage?.innerText) {
          safeSpeak(limitForSpeech(lastMessage.innerText), agentSay('La última respuesta de Qoph está vacía.', 'Qoph’s latest response is empty.'));
          return;
        }
        const history = document.getElementById('qoph-chat-history');
        if (history?.innerText) {
          safeSpeak(limitForSpeech(history.innerText), agentSay('No encontré historial legible de Qoph.', 'I could not find readable Qoph history.'));
          return;
        }
      }

      if (path === '/subconciencia') {
        const loadingNode = document.querySelector('.subconciencia-dream-loader, [aria-live="polite"][role="status"]');
        if (loadingNode) {
          speak(agentSay('Subconciencia todavía está cargando; no hay contenido legible aún.', 'Subconsciousness is still loading; there is no readable content yet.'));
          return;
        }
        const prompt = document.querySelector('.subconciencia-dream-prompt')?.innerText;
        const question = document.querySelector('.subconciencia-question-panel h2')?.innerText;
        const insight = document.querySelector('.subconciencia-insight')?.innerText;
        const options = Array.from(document.querySelectorAll('.subconciencia-choice-text, .subconciencia-choice-short'))
          .map((el) => el.innerText)
          .filter(Boolean);
        const readable = [prompt, question, insight, options.length ? agentSay(`Opciones: ${options.join(' / ')}`, `Options: ${options.join(' / ')}`) : '']
          .filter(Boolean)
          .join('. ');
        safeSpeak(limitForSpeech(readable), agentSay('Todavía no hay contenido legible en Subconciencia.', 'There is no readable Subconsciousness content yet.'));
        return;
      }

      if (path === '/') {
        const lead = document.getElementById('mutant-box-lead') || document.querySelector('[data-agent-readable="mutant-lead"]') || document.getElementById('mutant-box-current');
        textToRead = lead?.innerText || document.querySelector('.mutantbox-message-content')?.innerText;
      } else if (path === '/conciencia') {
        const q = document.querySelector('.question-box h2')?.innerText;
        const r = document.querySelector('.question-reflection')?.innerText;
        textToRead = `${q || ''}. ${r || ''}`;
      } else if (path.startsWith('/tareas')) {
        textToRead = document.querySelector('.active-task-container h2')?.innerText;
      } else if (path === '/questionnaire') {
        textToRead = document.getElementById('questionnaire-current-question')?.innerText || document.querySelector('.question-card h2')?.innerText;
      } else if (path.startsWith('/lugares')) {
        textToRead = document.getElementById('places-main')?.innerText;
      } else if (path.startsWith('/profile')) {
        textToRead = document.getElementById('profile-summary')?.innerText || document.getElementById('profile-main')?.innerText;
      } else if (path === '/ajustes') {
        textToRead = document.getElementById('settings-main')?.innerText;
      } else if (path === '/hiperstision') {
        textToRead = document.getElementById('hyperstition-current')?.innerText || document.getElementById('hyperstition-main')?.innerText;
      }

      safeSpeak(textToRead, agentSay("No encontré nada relevante para leer en esta pantalla.", "I did not find anything relevant to read on this screen."));
    } catch (error) {
      console.error('[AgentQ][READ_SCREEN]', error);
      speak(agentSay('Tuve un problema al leer la pantalla.', 'I had a problem reading the screen.'));
    }
  }, [agentSay, limitForSpeech, safeSpeak, speak]);

  const handleUserDataInput = useCallback(async (payload) => {
    if (!payload || !payload.text) return;

    if (window.location.pathname !== '/profile') {
      navigate('/profile');
    }

    try {
      const input = await waitForElement('#profile-feedback-input');
      setNativeValue(input, payload.text);
      triggerAgentFeedback();
      input.focus();
      // Nota: No se envía automáticamente según requerimientos.
    } catch (e) {
      console.error('[Orchestrator] Error en redirección de datos de perfil:', e);
    }
  }, [navigate]);

  const handleHyperstitionOps = useCallback((payload={}) => { if (payload.action === 'read_hyperstition' || payload.action==='read_last_hyperstition') safeSpeak(document.getElementById('hyperstition-current')?.innerText || document.getElementById('hyperstition-main')?.innerText, agentSay('No encontré hipersticiones visibles.', 'I could not find visible hyperstitions.')); }, [agentSay, safeSpeak]);
  const handleSettingsOps = useCallback((payload={}) => {
    if (isSensitiveAction(payload.action) && !payload.confirmed) return speak(agentSay('Necesito una confirmación más clara para hacer eso.', 'I need clearer confirmation to do that.'));
    if (payload.action === 'read_settings') return safeSpeak(document.getElementById('settings-main')?.innerText, agentSay('No encontré ajustes legibles.', 'I could not find readable settings.'));
    const result = applyAgentSetting(payload);
    if (result.ok) {
      triggerAgentFeedback();
      if (result.kind === 'theme') {
        return speak(result.value === 'light'
          ? agentSay('Activé el modo claro.', 'Light mode is on.')
          : agentSay('Activé el modo oscuro.', 'Dark mode is on.'));
      }
      if (result.kind === 'cyberpunk') {
        return speak(result.value
          ? agentSay('Activé cyberpunk.', 'Cyberpunk mode is on.')
          : agentSay('Desactivé cyberpunk.', 'Cyberpunk mode is off.'));
      }
      if (result.kind === 'solarpunk') {
        return speak(result.value
          ? agentSay('Activé solarpunk.', 'Solarpunk mode is on.')
          : agentSay('Desactivé solarpunk.', 'Solarpunk mode is off.'));
      }
    }
    if (payload.action === 'save_settings') {
      const b = document.getElementById('btn-save-settings');
      if (b) { triggerAgentFeedback(); b.click(); return true; }
    }
    speak(agentSay('No pude aplicar ese ajuste todavía.', 'I could not apply that setting yet.'));
    return false;
  }, [agentSay, safeSpeak, speak]);

  const handleNotificationsOps = useCallback(async (payload={}) => {
    const action = payload.action || 'open';
    const commandByAction = {
      open: 'notifications_open',
      close: 'notifications_close',
      read: 'notifications_read',
      mark_read: 'notifications_mark_read',
      delete_all: 'notifications_delete_all'
    };
    const commandId = commandByAction[action];
    if (!commandId) return speak(agentSay('No pude ejecutar esa acción de notificaciones todavía.', 'I cannot execute that notification action yet.'));
    try {
      const result = await executeCommandWithAwait(commandId, null, 4000);
      const ok = typeof result === 'object' ? result.ok !== false : Boolean(result);
      if (!ok) throw new Error(`${commandId} returned false`);
      if (action === 'read') return safeSpeak(result?.text, agentSay('No hay notificaciones para leer.', 'There are no notifications to read.'));
      if (action === 'open') return speak(agentSay('Abrí las notificaciones.', 'I opened notifications.'));
      if (action === 'close') return speak(agentSay('Cerré las notificaciones.', 'I closed notifications.'));
      if (action === 'mark_read') return speak(agentSay('Marqué las notificaciones como leídas.', 'I marked notifications as read.'));
      if (action === 'delete_all') return speak(agentSay('Limpié las notificaciones.', 'I cleared notifications.'));
    } catch (error) {
      console.error('[AgentQ][NOTIFICATIONS_OPS]', error);
      speak(agentSay('Todavía no puedo abrir la campana real de notificaciones.', 'I cannot control the real notification bell yet.'));
    }
  }, [agentSay, executeCommandWithAwait, safeSpeak, speak]);

  const handleGalleryOps = useCallback((payload={}) => { if (payload.action==='read_gallery'){const items=Array.from(document.querySelectorAll('[data-agent-gallery-title]')).slice(0,8).map(el=>el.getAttribute('data-agent-gallery-title')); safeSpeak(items.join(', '), agentSay('No encontré elementos de galería.', 'I could not find gallery items.'));} if (payload.action==='next_item'){const b=document.getElementById('btn-gallery-next'); if(b){triggerAgentFeedback(); b.click();}} if (payload.action==='previous_item'){const b=document.getElementById('btn-gallery-prev'); if(b){triggerAgentFeedback(); b.click();}} }, [agentSay, safeSpeak]);

  const handleAgentAction = useCallback(async (actionResponse) => {
    // Validación básica de seguridad
    if (!actionResponse || !actionResponse.type) {
      console.warn('⚠️ [Orchestrator] Respuesta inválida o vacía del agente.');
      return;
    }

    console.log('🤖 [Orchestrator] Ejecutando:', actionResponse);

    const type = actionResponse.type || actionResponse.category;
    const target = actionResponse.target;
    const payload = actionResponse.payload || actionResponse;

    if (actionResponse.speech && shouldSpeakBeforeExecution(type)) {
      speak(actionResponse.speech);
    }

    // 2. SISTEMA MOTOR (Ejecución de Acciones)
    const forcedRoute = routeForAction(type, payload);
    if (forcedRoute && window.location.pathname !== forcedRoute && ['NETWORK_OPS','QOPH_OPS','TASKS_OPS','PLACES_OPS','PROFILE_OPS','CONSCIOUSNESS_OPS','CONCIENCIA_OPS','MUTANT_BOX_OPS','HYPERSTITION_OPS','QUESTIONNAIRE_OPS','GALLERY_OPS'].includes(type)) {
      enqueuePendingAction(pendingRouteAction, { type, payload }, forcedRoute);
      navigate(forcedRoute);
      return;
    }

    switch (type) {
      case 'NAVIGATE':
        if (target) {
          const isDifferentPath = window.location.pathname !== target;

          if (payload && payload.deferred_execution) {
            enqueuePendingAction(pendingDeferredAction, payload.deferred_execution, target);
            console.log("Deferred action stored:", payload.deferred_execution);
          }

          if (isDifferentPath) {
            navigate(target);
          }

          if (payload && payload.scrollTarget) {
            if (isDifferentPath) {
              waitForElement(payload.scrollTarget)
                .then(el => {
                  triggerAgentFeedback();
                  el.scrollIntoView({ behavior: 'smooth' });
                })
                .catch(err => console.error(`[Orchestrator] Fallo al desplazar a ${payload.scrollTarget}:`, err));
            } else {
              const scrollTarget = document.querySelector(payload.scrollTarget);
              if (scrollTarget) {
                triggerAgentFeedback();
                scrollTarget.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }
        }
        break;

      case 'USER_DATA_INPUT':
        handleUserDataInput(payload);
        break;

      case 'TASK_OPS':
        return await handleTaskOps(payload);

      case 'CONSCIOUSNESS_INTERACT':
        handleConsciousnessOps(payload);
        break;

      case 'MUTANT_ACTION':
        handleMutantOps(payload);
        break;

      case 'PROFILE_OPS':
        handleProfileOps(payload);
        break;

      case 'QUESTIONNAIRE_OPS':
        handleQuestionnaireOps(payload);
        break;

      case 'SPHERE_OPS':
        if (window.location.pathname === '/conciencia') {
          const action = payload.action;
          const text = (payload.text || '').toLowerCase();
          const target = (payload.target || '').toLowerCase();
          const isSecondary = text.includes('secundaria') || text.includes('pequeña') || text.includes('pequena') || text.includes('inspiracion') || target.includes('secundaria') || target.includes('pequeña') || target.includes('pequena') || target.includes('inspiracion');

          if (action === 'rotate' || action === 'read_options') {
            return await handleConcienciaOps({
              action: isSecondary ? 'cycle_secondary_options' : 'cycle_main_options',
              route: '/conciencia',
              mode: 'loop'
            });
          }

          if (action === 'select' && text) {
             const navKeys = ['siguiente', 'proxima', 'anterior', 'primera', 'primero', 'ultima', 'ultimo', 'next', 'previous', 'first', 'last'];
             const foundKey = navKeys.find(k => text.includes(k));
             if (foundKey) {
                let targetIndex = null;
                if (['siguiente', 'proxima', 'next'].includes(foundKey)) targetIndex = 'next';
                else if (['anterior', 'previous'].includes(foundKey)) targetIndex = 'previous';
                else if (['primera', 'primero', 'first'].includes(foundKey)) targetIndex = 0;
                else if (['ultima', 'ultimo', 'last'].includes(foundKey)) targetIndex = 'last';

                if (targetIndex !== null) {
                   return await handleConcienciaOps({
                     action: isSecondary ? 'set_secondary_option' : 'set_main_option',
                     targetIndex,
                     route: '/conciencia'
                   });
                }
             }
          }
        }
        handleSphereOps(payload);
        break;

      case 'READ_SCREEN':
        return handleReadScreen();
      case 'NETWORK_OPS':
        return await handleNetworkOps(payload);
      case 'QOPH_OPS':
        return await handleQophOps(payload);
      case 'TASKS_OPS':
        return await handleTasksOps(payload);
      case 'PLACES_OPS':
        handlePlacesOps(payload);
        break;
      case 'CONCIENCIA_OPS':
        return await handleConcienciaOps(payload);
      case 'CONSCIOUSNESS_OPS':
        handleConsciousnessPageOps(payload);
        break;
      case 'MUTANT_BOX_OPS':
        handleMutantBoxOps(payload);
        break;
      case 'HYPERSTITION_OPS':
        handleHyperstitionOps(payload);
        break;
      case 'SETTINGS_OPS':
        return handleSettingsOps(payload);
      case 'NOTIFICATIONS_OPS':
        return await handleNotificationsOps(payload);
      case 'GALLERY_OPS':
        handleGalleryOps(payload);
        break;

      case 'CONVERSATION':
        // En conversación pura, solo el TTS (arriba) es necesario.
        navigate('/qoph', { state: { agenticPrompt: actionResponse.originalText || actionResponse.speech } });
        break;

      default:
        console.warn(`[Orchestrator] Tipo de acción desconocido: ${type}`);
        break;
    }
  }, [enqueuePendingAction, handleConcienciaOps, handleConsciousnessOps, handleConsciousnessPageOps, handleGalleryOps, handleHyperstitionOps, handleMutantBoxOps, handleMutantOps, handleNetworkOps, handleNotificationsOps, handlePlacesOps, handleProfileOps, handleQophOps, handleQuestionnaireOps, handleReadScreen, handleSettingsOps, handleSphereOps, handleTaskOps, handleTasksOps, handleUserDataInput, navigate, routeForAction, speak]);

  useEffect(() => {
    handleAgentActionRef.current = handleAgentAction;
  }, [handleAgentAction]);

  const processLocalAction = useCallback((transcript) => {
    if (!transcript) return false;
    const text = transcript.toLowerCase();

    if (agentMode === 'AWAITING_TASK_CONFIRMATION') {
      if (text.includes('sí') || text.includes('si') || text.includes('yes') || text.includes('save') || text.includes('confirm') || text.includes('dale') || text.includes('confirma') || text.includes('guarda')) {
        const btn = document.getElementById('btn-task-confirm');
        if (btn) {
          triggerAgentFeedback();
          btn.click();
          speak(agentSay("Tarea guardada.", "Task saved."));
        }
        setAgentMode('IDLE');
        return true;
      }

      if (text.includes('no') || text.includes('cancel') || text.includes('discard') || text.includes('cancela') || text.includes('borra')) {
        const input = document.getElementById('task-main-input');
        if (input) {
          triggerAgentFeedback();
          setNativeValue(input, '');
          speak(agentSay("Entendido, cancelado.", "Understood, canceled."));
        }
        setAgentMode('IDLE');
        return true;
      }
    }

    return false;
  }, [agentMode, agentSay, speak]);

  useEffect(() => {
    window.addEventListener('q-agent-cancel', clearAgentState);
    return () => window.removeEventListener('q-agent-cancel', clearAgentState);
  }, [clearAgentState]);

  useEffect(() => {
    return () => clearPendingTimeouts();
  }, [clearPendingTimeouts]);

  useEffect(() => {
    if (!location.pathname.startsWith('/tareas') && agentMode !== 'IDLE') {
      setAgentMode('IDLE');
    }
  }, [agentMode, location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const runPending = async (ref) => {
      const pending = ref.current;
      const status = getPendingAgentActionStatus(pending, location.pathname);
      if (status.status === 'empty' || status.status === 'wait') return false;
      ref.current = null;
      setPendingActionVersion((version) => version + 1);
      if (status.status === 'expired' || status.status === 'clear') return false;
      await new Promise((resolve) => setTimeout(resolve, 250));
      if (cancelled) return false;
      await Promise.resolve(handleAgentActionRef.current?.(status.action));
      return true;
    };

    (async () => {
      const routeActionExecuted = await runPending(pendingRouteAction);
      if (!routeActionExecuted) await runPending(pendingDeferredAction);
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, pendingActionVersion]);

  return {
    handleAgentAction,
    agentMode,
    setAgentMode,
    processLocalAction,
    speak,
    clearAgentState
  };
};
